/**
 * useContentDragDrop - 目录树拖拽排序逻辑
 * 从 ContentEdit.tsx 提取，封装拖拽排序相关的树操作函数和事件处理
 */
import { useState, useCallback, useRef } from 'react';
import { OutlineData, OutlineItem } from '../types';
import { projectApi } from '../services/api';

interface UseContentDragDropProps {
  outlineData: OutlineData | null;
  projectId?: number;
  onOutlineUpdated?: (outlineData: OutlineData) => void;
  section?: string;
}

export function useContentDragDrop({ outlineData, projectId, onOutlineUpdated, section = 'tech' }: UseContentDragDropProps) {
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: 'before' | 'inside' | 'after' } | null>(null);
  const dragNodeRef = useRef<OutlineItem | null>(null);

  // 在树中查找节点
  const findItemInTree = useCallback((items: OutlineItem[], targetId: string): OutlineItem | null => {
    for (const item of items) {
      if (item.id === targetId) return item;
      if (item.children && item.children.length > 0) {
        const found = findItemInTree(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // 从树中移除指定节点
  const removeItemFromTree = useCallback((items: OutlineItem[], targetId: string): { items: OutlineItem[]; removed: OutlineItem | null } => {
    const result: OutlineItem[] = [];
    let removed: OutlineItem | null = null;
    for (const item of items) {
      if (item.id === targetId) {
        removed = item;
        continue;
      }
      if (item.children && item.children.length > 0) {
        const res = removeItemFromTree(item.children, targetId);
        if (res.removed) {
          removed = res.removed;
          result.push({ ...item, children: res.items.length > 0 ? res.items : undefined });
        } else {
          result.push(item);
        }
      } else {
        result.push(item);
      }
    }
    return { items: result, removed };
  }, []);

  // 判断 candidateId 是否是 ancestorId 的后代
  const isDescendantOf = useCallback((items: OutlineItem[], candidateId: string, ancestorId: string): boolean => {
    if (candidateId === ancestorId) return false;
    const searchInSubtree = (list: OutlineItem[], target: string): boolean => {
      for (const item of list) {
        if (item.id === target) return true;
        if (item.children && item.children.length > 0) {
          if (searchInSubtree(item.children, target)) return true;
        }
      }
      return false;
    };
    const findAndCheck = (list: OutlineItem[], ancId: string, candId: string): boolean => {
      for (const item of list) {
        if (item.id === ancId) {
          if (item.children) return searchInSubtree(item.children, candId);
          return false;
        }
        if (item.children && item.children.length > 0) {
          if (findAndCheck(item.children, ancId, candId)) return true;
        }
      }
      return false;
    };
    return findAndCheck(items, ancestorId, candidateId);
  }, []);

  // 深度优先重新编号所有节点
  const renumberTree = useCallback((items: OutlineItem[], parentPrefix: string = ''): OutlineItem[] => {
    return items.map((item, index) => {
      const newId = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
      return {
        ...item,
        id: newId,
        children: item.children && item.children.length > 0
          ? renumberTree(item.children, newId)
          : undefined,
      };
    });
  }, []);

  // 在树中插入节点
  const insertItemToTree = useCallback((
    items: OutlineItem[],
    itemToInsert: OutlineItem,
    targetId: string,
    position: 'before' | 'after' | 'inside'
  ): OutlineItem[] => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const left = items.slice(0, i);
      const right = items.slice(i + 1);
      if (item.id === targetId) {
        if (position === 'before') return [...left, itemToInsert, item, ...right];
        if (position === 'after') return [...left, item, itemToInsert, ...right];
        const newChildren = item.children ? [...item.children, itemToInsert] : [itemToInsert];
        return [...left, { ...item, children: newChildren }, ...right];
      }
      if (item.children && item.children.length > 0) {
        const newChildren = insertItemToTree(item.children, itemToInsert, targetId, position);
        if (newChildren !== item.children) {
          return [...left, { ...item, children: newChildren }, ...right];
        }
      }
    }
    return items;
  }, []);

  // 拖拽事件处理
  const handleNodeDragStart = useCallback((e: React.DragEvent, item: OutlineItem) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    dragNodeRef.current = item;
    requestAnimationFrame(() => setDragNodeId(item.id));
  }, []);

  const handleNodeDragEnd = useCallback(() => {
    setDragNodeId(null);
    setDropTarget(null);
    dragNodeRef.current = null;
    document.body.style.cursor = '';
  }, []);

  const handleNodeDragOver = useCallback((e: React.DragEvent, item: OutlineItem) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (!dragNodeRef.current || dragNodeRef.current.id === item.id) {
      setDropTarget(null);
      return;
    }
    if (outlineData && isDescendantOf(outlineData.outline, item.id, dragNodeRef.current.id)) {
      setDropTarget(null);
      return;
    }
    const contentEl = (e.currentTarget as HTMLElement).querySelector<HTMLElement>('[draggable="true"]');
    const rect = (contentEl || e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    let position: 'before' | 'inside' | 'after';
    if (y < h * 0.3) position = 'before';
    else if (y > h * 0.7) position = 'after';
    else position = 'inside';
    setDropTarget({ id: item.id, position });
  }, [outlineData, isDescendantOf]);

  const handleNodeDragLeave = useCallback((e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !(e.currentTarget as HTMLElement).contains(relatedTarget)) {
      setDropTarget(null);
    }
  }, []);

  const handleNodeDrop = useCallback(async (e: React.DragEvent, targetItem: OutlineItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragNodeRef.current || !outlineData || !dropTarget) return;
    if (dragNodeRef.current.id === targetItem.id) return;
    if (isDescendantOf(outlineData.outline, targetItem.id, dragNodeRef.current.id)) {
      handleNodeDragEnd();
      return;
    }
    const draggedItem = { ...dragNodeRef.current };
    const { items: afterRemove } = removeItemFromTree(outlineData.outline, draggedItem.id);
    const newTree = insertItemToTree(afterRemove, draggedItem, dropTarget.id, dropTarget.position);
    const renumberedTree = renumberTree(newTree);
    const updatedOutlineData: OutlineData = { ...outlineData, outline: renumberedTree };
    if (onOutlineUpdated) onOutlineUpdated(updatedOutlineData);
    if (projectId) {
      const flatten = (items: OutlineItem[], parentId: string | null = null): any[] => {
        let result: any[] = [];
        for (const item of items) {
          result.push({
            id: item.id, parent_node_id: parentId,
            title: item.title, description: item.description || '',
            word_count_range: item.word_count_range || '',
            source_requirement_id: item.source_requirement_id || undefined,
            source_requirement_title: item.source_requirement_title || undefined,
            section,
          });
          if (item.children && item.children.length > 0) {
            result = result.concat(flatten(item.children, item.id));
          }
        }
        return result;
      };
      projectApi.saveOutline(projectId, flatten(renumberedTree)).catch(() => {});
    }
    handleNodeDragEnd();
  }, [outlineData, dropTarget, projectId, onOutlineUpdated, section, removeItemFromTree, insertItemToTree, renumberTree, isDescendantOf, handleNodeDragEnd]);

  return {
    dragNodeId, dropTarget, dragNodeRef,
    findItemInTree, removeItemFromTree, isDescendantOf, renumberTree, insertItemToTree,
    handleNodeDragStart, handleNodeDragEnd, handleNodeDragOver, handleNodeDragLeave, handleNodeDrop,
  };
}
